import { useState, useCallback } from "react";

export type CertifierFormData = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  extension: string;
};

type UseSubmissionParams = {
  certificationStatus: string;
  certificationStep: string;
  certifierFirstName?: string;
  certifierLastName?: string;
  certifiedDate?: string;
  onCertify?: (data: CertifierFormData) => Promise<boolean> | boolean | void;
  onDecertify?: () => void;
};

function formatCertifiedDate(raw: string | undefined): string {
  if (!raw) return "";
  const hasTimezone = /Z|[+-]\d{2}:?\d{2}$/.test(raw);
  const d = new Date(hasTimezone ? raw : raw + "Z");
  if (isNaN(d.getTime())) return "";
  const date = d.toLocaleDateString("en-US");
  const time = d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return `${date} at ${time}`;
}

export default function useSubmission({
  certificationStatus,
  certificationStep,
  certifierFirstName,
  certifierLastName,
  certifiedDate,
  onCertify,
  onDecertify,
}: UseSubmissionParams) {
  const [justCertified, setJustCertified] = useState(false);
  const [justDecertified, setJustDecertified] = useState(false);
  const [localCertifierName, setLocalCertifierName] = useState("");
  const [localCertifiedDate, setLocalCertifiedDate] = useState("");
  const [showCertifyToast, setShowCertifyToast] = useState(false);
  const [showDecertifyToast, setShowDecertifyToast] = useState(false);
  const [showDecertifyDialog, setShowDecertifyDialog] = useState(false);

  const isCertified =
    !justDecertified && (certificationStatus === "Certified" || justCertified);

  const displayStep = justCertified
    ? "Certified"
    : justDecertified
      ? "Ready for Certification"
      : certificationStep;

  const certifierName = justCertified
    ? localCertifierName
    : `${certifierFirstName ?? ""} ${certifierLastName ?? ""}`.trim();

  // When justCertified, use the local ISO string we captured at certify time (already formatted).
  // After a refresh, certifiedDate comes from the API without a timezone suffix — apply UTC fix.
  const displayCertifiedDate = justCertified
    ? localCertifiedDate
    : formatCertifiedDate(certifiedDate);

  const handleCertify = useCallback(
    async (data: CertifierFormData) => {
      const result = await onCertify?.(data);
      if (result === false) return;
      setLocalCertifierName(`${data.firstName} ${data.lastName}`);
      setLocalCertifiedDate(formatCertifiedDate(new Date().toISOString()));
      setJustCertified(true);
      setJustDecertified(false);
      setShowCertifyToast(true);
    },
    [onCertify]
  );

  const openDecertifyDialog = useCallback(() => {
    setShowDecertifyDialog(true);
  }, []);

  const closeDecertifyDialog = useCallback(() => {
    setShowDecertifyDialog(false);
  }, []);

  const confirmDecertify = useCallback(() => {
    setShowDecertifyDialog(false);
    onDecertify?.();
    setJustCertified(false);
    setJustDecertified(true);
    setShowDecertifyToast(true);
  }, [onDecertify]);

  const dismissCertifyToast = useCallback(() => {
    setShowCertifyToast(false);
  }, []);

  const dismissDecertifyToast = useCallback(() => {
    setShowDecertifyToast(false);
  }, []);

  return {
    isCertified,
    displayStep,
    certifierName,
    displayCertifiedDate,
    showCertifyToast,
    showDecertifyToast,
    dismissCertifyToast,
    dismissDecertifyToast,
    showDecertifyDialog,
    openDecertifyDialog,
    closeDecertifyDialog,
    handleCertify,
    confirmDecertify,
  };
}
